import axios from 'axios';
import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { Modal, ModalBody, ModalHeader, Row, Col, ModalFooter, Button } from 'reactstrap';
import { Formik, Form, Field, ErrorMessage, useFormikContext, FieldArray } from 'formik';
import Loader from 'react-loader-spinner';
import * as Yup from 'yup';


// Component to render dynamic tier inputs
const TierPricingFields = () => {
  const { values, errors, touched } = useFormikContext();

  return (
    <FieldArray name="tiers">
      {({ push, remove }) => (
        <div>
          {values.tiers && values.tiers.length > 0 && values.tiers.map((tier, index) => {
            const tierError = errors.tiers && errors.tiers[index];
            const tierTouched = touched.tiers && touched.tiers[index];
            const hasError = tierTouched && tierError && (tierError.tier || tierError.cost);

            return (
              <div key={index}>
                <Row className="my-2 align-items-end">
                  <Col md={5}>
                    <label className="form-label small">Tier {index + 1} (e.g., 0-1000, 1001-5000)</label>
                    <Field
                      type="text"
                      name={`tiers.${index}.tier`}
                      className={`form-control ${tierTouched && tierError?.tier ? 'is-invalid' : ''}`}
                      placeholder="Enter tier range"
                    />
                  </Col>
                  <Col md={5}>
                    <label className="form-label small">Cost per record</label>
                    <Field
                      type="text"
                      name={`tiers.${index}.cost`}
                      className={`form-control ${tierTouched && tierError?.cost ? 'is-invalid' : ''}`}
                      placeholder="Enter cost"
                    />
                  </Col>
                  <Col md={2}>
                    {values.tiers.length > 1 && (
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => remove(index)}
                        className="w-100"
                      >
                        Remove
                      </Button>
                    )}
                  </Col>
                </Row>
                {hasError && (
                  <Row className="mb-2">
                    <Col md={12}>
                      {tierError?.tier && (
                        <p className="m-0 error-msg">{tierError.tier}</p>
                      )}
                      {tierError?.cost && (
                        <p className="m-0 error-msg">{tierError.cost}</p>
                      )}
                    </Col>
                  </Row>
                )}
              </div>
            );
          })}
          <Row className="my-2">
            <Col>
              <Button
                color="primary"
                size="sm"
                onClick={() => push({ tier: '', cost: '' })}
                type="button"
              >
                Add Tier
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </FieldArray>
  );
};

export default function EditSitePricingForm({ data, setToggle, siteStatuses, feasibilityStatuses }) {

  const [modal, setModal] = useState(false);
  const [loadingBtns, setLoadingBtns] = useState(false);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState({
    monthly_maintenance_fee: '',
    pricing_strategy: '',
    flat_volume_charges: '',
    tiers: [{ tier: '', cost: '' }]
  });
  const formikRef = useRef(null);

  const fetchBillingSummary = () => {
    setLoadingBilling(true);
    axios.get(`/api/v1/admins/feasibility_reports/${data.site_id}/billing_summary`)
      .then(res => {
        const billing = res.data?.billing_data;
        if (billing) {
          const values = {
            monthly_maintenance_fee: billing.monthly_maintenance_fee ?? '',
            pricing_strategy: billing.pricing_strategy || '',
            flat_volume_charges: billing.pricing_strategy === 'flat_pricing' ? (billing.volume_charges ?? '') : '',
            tiers: billing.pricing_strategy === 'tier_pricing' && billing.tiers?.length > 0
              ? billing.tiers.map(t => ({ tier: t.tier, cost: t.cost }))
              : [{ tier: '', cost: '' }]
          };
          setInitialFormValues(values);
        }
        setLoadingBilling(false);
      }).catch(err => {
        console.error('Failed to fetch billing summary, using defaults:', err);
        toast.warn('Could not load current pricing, showing defaults');
        setLoadingBilling(false);
      });
  };

  const toggleFunc = () => {
    if (!modal) {
      fetchBillingSummary();
    }
    setModal(curr => !curr);
  };

  const closeBtn = <button className="close" onClick={() => setModal(false)}> &times; </button>;

  // Helper to convert string to number if needed
  const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? '' : parsed;
    }
    return value;
  };

  const updateSiteDetails = (values) => {
    setLoadingBtns(true);

    // Prepare pricing_detail_attributes with separate fields
    const pricingDetailAttributes = {
      monthly_maintenance_fee: parseNumber(values.monthly_maintenance_fee) || 0,
      pricing_strategy: values.pricing_strategy
    };

    if (values.pricing_strategy === 'tier_pricing') {
      // Send tiers as separate field (filter out empty tiers)
      pricingDetailAttributes.tiers = values.tiers.filter(tier => tier.tier && tier.cost).map(tier => ({
        tier: tier.tier,
        cost: parseNumber(tier.cost)
      }));
      // volume_charges can be null or empty for tier pricing
      pricingDetailAttributes.volume_charges = null;
    } else {
      // For flat pricing, send volume_charges as number
      pricingDetailAttributes.volume_charges = parseNumber(values.flat_volume_charges) || 0;
      // tiers not needed for flat pricing
    }

    axios.put(`/api/v1/admins/feasibility_reports/${data.site_id}/update_pricing`, {
      pricing_detail: pricingDetailAttributes
    }).then(res => {
      toast.success(res.data.message);
      setModal(false);
      setToggle(curr => !curr);
      setLoadingBtns(false);
    }).catch(err => {
      toast.error(err?.response?.data?.message || 'Failed to update pricing');
      setLoadingBtns(false);
    });
  }

  // Dynamic validation schema based on pricing strategy
  const getValidationSchema = () => {
    return Yup.object({
      monthly_maintenance_fee: Yup.number().typeError("Monthly maintenance fee must be a number"),
      pricing_strategy: Yup.string().required("Pricing strategy is required"),
      flat_volume_charges: Yup.number().when('pricing_strategy', {
        is: 'flat_pricing',
        then: (schema) => schema.typeError("Volume charges must be a number").required("Volume charges is required"),
        otherwise: (schema) => schema.notRequired()
      }),
      tiers: Yup.array().when('pricing_strategy', {
        is: 'tier_pricing',
        then: (schema) => schema
          .min(1, "At least one tier is required")
          .of(
            Yup.object().shape({
              tier: Yup.string()
                .required("Tier range is required")
                .matches(
                  /^\d+\s*-\s*(\d+|\*)$|^\d+\+$/,
                  "Tier must be in range format (e.g., '0-1000', '1001-5000', '0-*', or '5000+')"
                ),
              cost: Yup.number().typeError("Cost must be a number").required("Cost is required")
            })
          ),
        otherwise: (schema) => schema.notRequired()
      })
    });
  }


  return (
    <>
      <Modal isOpen={modal} toggle={toggleFunc} backdrop='static' size='lg'>
        <ModalHeader toggle={toggleFunc} close={closeBtn}>
          Edit {data.site_name}
        </ModalHeader>
        <ModalBody className='px-5'>
          {loadingBilling ? (
            <div className="text-center py-4">
              <Loader type="Puff" color="#00BFFF" height={60} width={60} className='pc-loader' />
              <p className="mt-2 text-muted">Loading current pricing...</p>
            </div>
          ) : (
          <Formik
            innerRef={formikRef}
            enableReinitialize={true}
            initialValues={initialFormValues}
            validationSchema={getValidationSchema()}
            onSubmit={(values) => updateSiteDetails(values)}
          >
            {({ isSubmitting, values, setFieldValue }) => (
              <Form>
                <Row className='my-2'>
                  <Col lg={4}>
                    <h6 className='my-0'>Monthly Maintenance Fee</h6>
                  </Col>
                  <Col lg={8}>
                    <Field type="text" name="monthly_maintenance_fee" className="form-control" />
                    <ErrorMessage name="monthly_maintenance_fee" component="p" className="m-0 error-msg mt-1 mb-2"/>
                  </Col>
                </Row>
                <Row className='my-2'>
                  <Col lg={4}>
                    <h6 className='my-0'>Volume Pricing Strategy</h6>
                  </Col>
                  <Col md={8}>
                    <Field
                      as="select"
                      name="pricing_strategy"
                      className="form-control"
                      onChange={(e) => {
                        setFieldValue('pricing_strategy', e.target.value);
                        // Reset volume charges when strategy changes
                        if (e.target.value === 'flat_pricing') {
                          setFieldValue('tiers', [{ tier: '', cost: '' }]);
                        } else {
                          setFieldValue('flat_volume_charges', '');
                        }
                      }}
                    >
                      <option value="">Select Pricing Strategy</option>
                      {[["Flat pricing", "flat_pricing"], ["Tier pricing", "tier_pricing"]].map(each => (
                        <option value={each[1]} key={each[0]}>
                          {each[0]}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="pricing_strategy" component="p" className="m-0 error-msg mt-1 mb-2"/>
                  </Col>
                </Row>

                {values.pricing_strategy === 'flat_pricing' && (
                  <Row className='my-2'>
                    <Col lg={4}>
                      <h6 className='my-0'>Volume Charges (per record)</h6>
                    </Col>
                    <Col lg={8}>
                      <Field type="text" name="flat_volume_charges" className="form-control" placeholder="Enter cost per record" />
                      <ErrorMessage name="flat_volume_charges" component="p" className="m-0 error-msg mt-1 mb-2"/>
                    </Col>
                  </Row>
                )}

                {values.pricing_strategy === 'tier_pricing' && (
                  <Row className='my-2'>
                    <Col lg={12}>
                      <h6 className='my-0 mb-3'>Tier Pricing</h6>
                      <TierPricingFields />
                    </Col>
                  </Row>
                )}

                <ModalFooter>
                  <Button color='light' onClick={() => setModal(false)}>Close</Button>
                  <Button color="success" type="submit" disabled={loadingBtns || isSubmitting}>Update</Button>
                </ModalFooter>
              </Form>
            )}
          </Formik>
          )}
        </ModalBody>
      </Modal>
      <button className='btn btn-xs btn-primary mx-1' onClick={toggleFunc}>Request Pricing change</button>
    </>
  );
}
